// Carry Roll CSV import — per-vendor adapters (8.8). Pure parsing functions.

import { degToRad } from '@carry-roll/shared-logic';
import {
  ClubCategory,
  Fidelity,
  Firmness,
  LandingSurface,
  SurfaceMoisture,
  type ShotRecord,
  type SimulationRequest,
} from '@carry-roll/shared-types';

export type MonitorFormat = 'garmin' | 'skytrak' | 'mevo' | 'foresight' | 'rapsodo';

export interface CsvParseResult {
  shots: ShotRecord[];
  format: MonitorFormat;
}

const DEFAULT_ENV = {
  altitudeM: 0,
  temperatureK: 293.15,
  windSpeed: 0,
  windDirDeg: 0,
  firmness: Firmness.Normal,
  moisture: SurfaceMoisture.Normal,
  landingSurface: LandingSurface.Fairway,
  landingSlopeDeg: 0,
  crossSlopeDeg: 0,
  elevationDiffM: 0,
  seaLevelBaseline: false,
  humidity: 0.5,
  pressurePa: 101325,
};

/** Parse a CSV string into ShotRecord[] using the given monitor format. */
export function parseCsv(csv: string, format: MonitorFormat): CsvParseResult {
  const lines = csv.trim().split(/\r?\n/);
  if (lines.length < 2) return { shots: [], format };

  const header = lines[0]!.toLowerCase().split(',').map((h) => h.trim());
  const rows = lines.slice(1).map((l) => l.split(',').map((c) => c.trim()));

  const shots: ShotRecord[] = [];
  for (const row of rows) {
    const get = (name: string): string | undefined => {
      const idx = header.indexOf(name);
      return idx >= 0 ? row[idx] : undefined;
    };
    const num = (name: string): number => {
      const v = get(name);
      return v ? parseFloat(v) || 0 : 0;
    };

    const ballSpeed = num('ball speed') || num('ball_speed') || num('bs');
    const launchAngle = num('launch angle') || num('launch_angle') || num('la');
    const spin = num('spin rate') || num('spin') || num('backspin') || num('spin_rate');

    if (!ballSpeed && !launchAngle && !spin) continue;

    const request: SimulationRequest = {
      launch: {
        ballSpeed,
        launchAngle: degToRad(launchAngle),
        spinRpm: spin,
        clubSpeed: num('club speed') || num('club_speed') || null,
        spinAxisDeg: num('spin axis') || num('side spin') || 0,
        launchDirDeg: num('launch direction') || 0,
        apexHint: null,
      },
      delivery: null,
      environment: { ...DEFAULT_ENV },
      override: { landingAngleDeg: null },
      club: { category: ClubCategory.Driver, loftDeg: null, rollPercent: null },
      fidelity: Fidelity.Full,
      ballType: 'tour',
    };
    const carryM = num('carry') || num('carry distance') || 0;
    const totalM = num('total') || num('total distance') || carryM;

    shots.push({
      id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
      request,
      carryM,
      totalM,
      excluded: false,
    });
  }

  return { shots, format };
}

/** Generate a sample CSV for a monitor format (used for round-trip testing). */
export function sampleCsv(format: MonitorFormat): string {
  const headers: Record<MonitorFormat, string[]> = {
    garmin: ['Ball Speed', 'Launch Angle', 'Spin Rate', 'Carry', 'Total'],
    skytrak: ['ball_speed', 'launch_angle', 'spin', 'carry', 'total'],
    mevo: ['Ball Speed', 'Launch Angle', 'Spin Rate', 'Carry Distance', 'Total Distance'],
    foresight: ['Ball Speed', 'Launch Angle', 'Backspin', 'Carry', 'Total'],
    rapsodo: ['ball speed', 'launch angle', 'spin rate', 'carry', 'total'],
  };
  const h = headers[format];
  const rows = [
    [75, 14.1, 2700, 228, 256],
    [74, 13.8, 2750, 225, 252],
    [76, 14.5, 2650, 231, 260],
  ].map((r) => r.join(','));
  return [h.join(','), ...rows].join('\n');
}
